<template>
  <div class="stats-chart" v-if="data">
      <div class="timeline-heading">Your Last 10 Days</div>

      <stats-header 
        :current-metric="currentMetric" 
        :display-game-type="displayGameType"/>

      <div class="statistic-border" :style="{'border': `1px solid ${metricColor}`}"></div>

     <lineChart 
      v-if="data"
      :player="account"
      :color="metricColor"
      :data="data"
      :height="400"
      :y-ticks="getYTicks()"
      :y-unit="yUnit()"
      :y-axis-title="yAxisTitle()"
      :right-y-label-axis="rightYLabelAxis()"
      />
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart';
import { METRIC_COLOR, METRICS } from '@/constants/DashboardConstants';
import { SESSION_METRICS } from '@/constants/SessionConsts';
import StatsHeader from '@/components/dashboard/StatsHeader';
import { getUnitLabel } from '../../common/helper';


export default {
  name: 'TimelineChart',
  props : [
    'data',
    'currentMetric',
    'account'
  ],
  data () {
    return {
      displayGameType : true
    }
  },
   computed: {
    metricColor () {
      return METRIC_COLOR[this.currentMetric];
    }
  },

  methods : {
    getYTicks () {
      let highestValue = 0;
      let fixedYTicks;
      
      this.data.forEach(t => {
        if(t.value > highestValue) {
          highestValue = t.value;
        }
      });

      switch(this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          fixedYTicks = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45];

          if(highestValue > fixedYTicks[fixedYTicks.length - 1]) {
            while(fixedYTicks[fixedYTicks.length - 1] < highestValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 5);
            }
          }
          return fixedYTicks;
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          fixedYTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80];

          if(highestValue > fixedYTicks[fixedYTicks.length - 1]) {
            while(fixedYTicks[fixedYTicks.length - 1] < highestValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 10);
            }
          }

          return fixedYTicks; 
        case METRICS.DISTANCE:
          fixedYTicks = [0, 1250, 2500, 3750, 5000, 6250, 7500, 8750, 10000, 11250];

          if(highestValue > fixedYTicks[fixedYTicks.length - 1]) {
            while(fixedYTicks[fixedYTicks.length - 1] < highestValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 1250);
            }
          }
          
          return fixedYTicks;
        case METRICS.WORKLOAD:
          fixedYTicks = [0, 5, 10, 15, 20, 25, 30];

          if(highestValue > fixedYTicks[fixedYTicks.length - 1]) {
            while(fixedYTicks[fixedYTicks.length - 1] < highestValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 5);
            }
          }
          
          return fixedYTicks;
      }
    },
    yAxisTitle () {
      switch(this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return this.$t('DASHBOARD.lineChart.yAxisTitle.timeAbove80');
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return this.$t('DASHBOARD.lineChart.yAxisTitle.activeParticipation'); 
        case METRICS.DISTANCE:
          return this.$t('DASHBOARD.lineChart.yAxisTitle.distance');
        case METRICS.WORKLOAD:
          return this.$t('DASHBOARD.lineChart.yAxisTitle.workload');
      }
    },
    rightYLabelAxis () {
      switch(this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return { 
            array: [0, 7.5, 15, 22.5, 30, 37.5],
            labels: {
              '0': '',
              '7.5': this.$t('DASHBOARD.lineChart.rightYLabelAxis.low'),
              '15': '',
              '22.5': this.$t('DASHBOARD.lineChart.rightYLabelAxis.moderate'),
              '30': '',
              '37.5': this.$t('DASHBOARD.lineChart.rightYLabelAxis.high'),
              '45': ''
            }
          }
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return {
            array: [0, 20, 40, 50, 60, 70, 80],
            labels: {
              '0': '',
              '20': this.$t('DASHBOARD.lineChart.rightYLabelAxis.low'),
              '40': '',
              '50': this.$t('DASHBOARD.lineChart.rightYLabelAxis.moderate'),
              '60': '',
              '70': this.$t('DASHBOARD.lineChart.rightYLabelAxis.high'),
              '80': ''
            }
          } 
        case METRICS.DISTANCE:
          return {
            array: [0, 1250, 2500, 3750, 5000, 7500],
            labels: {
              '0': '',
              '1250': this.$t('DASHBOARD.lineChart.rightYLabelAxis.low'),
              '2500': '',
              '3750': this.$t('DASHBOARD.lineChart.rightYLabelAxis.moderate'),
              '5000': '',
              '7500': this.$t('DASHBOARD.lineChart.rightYLabelAxis.high')
            }
          } 
        case METRICS.WORKLOAD:
          return {
            array: [0, 5, 10, 15, 20, 25],
            labels: {
              '0': '',
              '5': this.$t('DASHBOARD.lineChart.rightYLabelAxis.low'),
              '10': '',
              '15': this.$t('DASHBOARD.lineChart.rightYLabelAxis.moderate'),
              '20': '',
              '25': this.$t('DASHBOARD.lineChart.rightYLabelAxis.high')
            }
          } 
      }
    },
    yUnit () {
      switch(this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return '%';
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return '%';
        case METRICS.DISTANCE:
          return ' ' + getUnitLabel(this.account.unitDistance);
        case METRICS.WORKLOAD:
          return '%';
      }
    }
  },
  components: {
    LineChart,
    StatsHeader
  }
}
</script>

<style lang="scss">
  
    .timeline-heading {
      padding-top: 30px;
      color: #5E5E62;
      font-weight: 600;
    }
  
</style>